import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import _ from 'root/libs/exaforte/react/lodash';
import TouchableArea from 'root/libs/exaforte/react/TouchableArea';

import { useTable, useBlockLayout, useExpanded } from 'react-table';
import { useSticky } from 'react-table-sticky';
import styles from './CustomTable.less';

export default React.memo(function CustomTable({columns, data, fetching, formatHeader, formatData, classNames, rowClassNamesCallback, cellClassNamesCallback, onRowClick, displayHeader, renderSubElement, onBottomReached, sticky, emptyResponseElement, loadingElement, onRowMouseEnter, onRowMouseLeave}) {
    const tableStyles = _.cloneDeep(styles);
    
    if(sticky)
    {
        if(!tableStyles.table)
            tableStyles.table = [];

        if(!_.isArray(tableStyles.table))
            tableStyles.table = [tableStyles.table];

        tableStyles.table.unshift(styles.sticky);
        
        tableStyles.table = tableStyles.table.join(' ');
    }

    for(let i in classNames)
        if(!tableStyles[i])
            tableStyles[i] = classNames[i];
        else
            tableStyles[i] += ' ' + classNames[i];

    let config = {
        columns:        columns,
        data:           data,
        defaultColumn:  {
            width: 110
        }
    }
    
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { 
            expanded
        },
    } = useTable(
        config,
        useBlockLayout,
        useSticky, 
        useExpanded
    );
    
    let handleScroll;

    if(onBottomReached)
        handleScroll = (e) => {
            const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    
            if(bottom && onBottomReached)
                onBottomReached();
        }
    
    if(!emptyResponseElement)
        emptyResponseElement = <Text className={[tableStyles['text-align-center'], tableStyles['py-1']]}>Geen items gevonden</Text>;

    if(!loadingElement)
        loadingElement = <ActivityIndicator size={'lg'} />;

    return (
        <View {...getTableProps()} className={[tableStyles.table]} {...(typeof onBottomReached != 'undefined') && {onScroll: handleScroll}}>
            {(displayHeader != false) && (
                <View className={[tableStyles.header]}>
                    {headerGroups.map((headerGroup) => {
                        return (
                            <View key={headerGroup} {...headerGroup.getHeaderGroupProps()} className={[tableStyles.tr]}>
                                {headerGroup.headers.map((column) => {
                                    if(formatHeader !== undefined)
                                        column.value = formatHeader(column);

                                    return (<View key={column} {...column.getHeaderProps()} className={[tableStyles.th, (column.width == 'auto' && tableStyles['th-auto-width']), ...(column.className || [])]}>
                                        {(typeof column.value == 'object' ? column.value : column.render('Header'))}
                                    </View>);
                                })}
                            </View>
                        );
                    })}
                </View>
            )}
            <View {...getTableBodyProps()} className={[tableStyles.body]}>
                {fetching ? loadingElement : (_.size(rows) == 0 ? emptyResponseElement : _.map(rows, (row, rowIndex) => {
                    prepareRow(row);
                    
                    if(row.original.isExpanded !== false)
                        row.isExpanded = row.subRows.length > 0;
                    
                    let rowProps    = row.getRowProps();
                    let classNames  = [tableStyles.tr];
                    let last_index  = _.size(row.cells) - 1;
                    
                    if(rowClassNamesCallback)
                        classNames = [...classNames, ...rowClassNamesCallback({row: row, index: rowIndex, rows: rows})];
                    
                    if(row.original.isSelected && tableStyles.selected)
                        classNames.push(tableStyles.selected);
                    
                    return (
                        <React.Fragment key={rowIndex}>
                            <View {...rowProps} className={classNames}>
                                {_.map(row.cells, (cell, cell_index) => {
                                    cell.column.first       = (cell_index == 0);
                                    cell.column.second      = (cell_index == 1);
                                    cell.column.second_last = (cell_index == (last_index - 1));
                                    cell.column.last        = (cell_index == last_index);

                                    let cellClassNames = (cell.column.classNames ? cell.column.classNames : []);
                                    
                                    if(cellClassNamesCallback)
                                        cellClassNames = [...cellClassNames, ...cellClassNamesCallback(cell, rowIndex)];
                                    
                                    if(formatData !== undefined)
                                        cell.value = formatData(cell.value, cell.column, cell.row.original, false, headerGroups[0].headers);
                                    
                                    let onClick = undefined;
                                    
                                    if(cell.column.onValueClick)
                                        onClick = cell.column.onValueClick;
                                    else if(onRowClick && !cell.column.disableRowClick)
                                        onClick = onRowClick;

                                    if(onClick)
                                        return (
                                            <TouchableArea onPress={() => {onClick(cell.row.original, cell.column.id)}} {...cell.getCellProps()} className={[tableStyles.td, (cell.column.width == 'auto' && tableStyles['td-auto-width']), ...cellClassNames]} {...{ ...( onRowMouseEnter && { onMouseEnter: () => onRowMouseEnter(cell.row.original, cell.column.id) } ), ...( onRowMouseEnter && { onMouseLeave: () => onRowMouseLeave(cell.row.original, cell.column.id) } ) }}>
                                                {(_.is_empty(cell.value) ? cell.column.defaultValue : (typeof cell.value == 'object' ? cell.value : <Text>{cell.render('Cell')}</Text>))}
                                            </TouchableArea>
                                        );
                                    else
                                        return (
                                            <View {...cell.getCellProps()} className={[tableStyles.td, (cell.column.width == 'auto' && tableStyles['td-auto-width']), ...cellClassNames]} {...{ ...( onRowMouseEnter && { onMouseEnter: () => onRowMouseEnter(cell.row.original, cell.column.id) } ), ...( onRowMouseEnter && { onMouseLeave: () => onRowMouseLeave(cell.row.original, cell.column.id) } ) }}>
                                                {(_.is_empty(cell.value) ? cell.column.defaultValue : (typeof cell.value == 'object' ? cell.value : <Text>{cell.render('Cell')}</Text>))}
                                            </View>
                                        );
                                })}
                            </View>
                            {/*
                                If the row is in an expanded state, render a row with a
                                column that fills the entire length of the table.
                              */}
                            {row.isExpanded ? _.map(row.subRows, (subRow, subRowIndex) => {
                                prepareRow(subRow);
                                
                                let subRowClassNames  = [tableStyles.tr];
                                
                                if(rowClassNamesCallback)
                                    subRowClassNames = [...subRowClassNames, ...rowClassNamesCallback({row: row, index: rowIndex, subRow: subRow})];
                                
                                if(row.original.isSelected && tableStyles.selected)
                                    subRowClassNames.push(tableStyles.selected);
                                
                                return (<View {...subRow.getRowProps()} className={subRowClassNames}>
                                    {(  
                                        renderSubElement !== undefined ? 
                                            renderSubElement(subRow.original, row)
                                        : 
                                            subRow.cells.map((cell) => {
                                                let cellClassNames = (cell.column.classNames ? cell.column.classNames : []);
                                                
                                                if(formatData !== undefined)
                                                    cell.value = formatData(cell.value, cell.column, cell.row.original, true, headerGroups[0].headers);
                                                
                                                let onClick = (onRowClick && !cell.column.disableRowClick ? onRowClick : undefined);
                                                
                                                if(onClick)
                                                    return (
                                                        <TouchableArea onPress={() => {onClick(cell.row.original, subRowIndex)}} {...cell.getCellProps()} className={[tableStyles.td, (cell.column.width == 'auto' && tableStyles['td-auto-width']), ...cellClassNames]}>
                                                            {(_.is_empty(cell.value) ? cell.column.defaultValue : (typeof cell.value == 'object' ? cell.value : <Text>{cell.render('Cell')}</Text>))}
                                                        </TouchableArea>
                                                    );
                                                else
                                                    return (
                                                        <View {...cell.getCellProps()} className={[tableStyles.td, ...cellClassNames]}>
                                                            {(_.is_empty(cell.value) ? cell.column.defaultValue : (typeof cell.value == 'object' ? cell.value : <Text>{cell.render('Cell')}</Text>))}
                                                        </View>
                                                    );
                                            })
                                    )}
                                </View>)
                            }) : null}
                        </React.Fragment>
                    );
                }))}
            </View>
        </View>
    );
}, (prevProps, nextProps) => _.isEqual(prevProps, nextProps));